import React from 'react';
import { Card, Row, Col, Select, Table, Button, Tag } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import moment from 'moment';
import { RequestTrackingResponse } from '@openapi';
import { formatTime } from '../utils/trackingUtils';

const { Option } = Select;

interface RequestsTableProps {
  viewMode: 'slowest' | 'errors';
  onViewModeChange: (mode: 'slowest' | 'errors') => void;
  requests: RequestTrackingResponse[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize: number) => void;
  onShowDetail: (request: RequestTrackingResponse) => void;
}

const RequestsTable: React.FC<RequestsTableProps> = ({
  viewMode,
  onViewModeChange,
  requests,
  loading,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onShowDetail,
}) => {
  const columns = [
    {
      title: 'MAC Address',
      dataIndex: 'macAddress',
      key: 'macAddress',
      render: (mac: string) => <Tag color="blue">{mac}</Tag>,
    },
    {
      title: 'API',
      dataIndex: 'apiVersion',
      key: 'apiVersion',
      render: (version: string) => <Tag color={version === 'v2' ? 'green' : 'orange'}>{version}</Tag>,
    },
    {
      title: 'Execution Time',
      dataIndex: 'executionTimeMs',
      key: 'executionTimeMs',
      render: (time: number) => (
        <span style={{ color: time > 5000 ? '#ff4d4f' : '#52c41a', fontWeight: 'bold' }}>{formatTime(time)}</span>
      ),
      sorter: (a: RequestTrackingResponse, b: RequestTrackingResponse) =>
        (a.executionTimeMs || 0) - (b.executionTimeMs || 0),
    },
    {
      title: 'Status',
      dataIndex: 'responseStatus',
      key: 'responseStatus',
      render: (status: number) => <Tag color={status < 400 ? 'success' : 'error'}>{status}</Tag>,
    },
    {
      title: 'Timestamp',
      dataIndex: 'requestTimestamp',
      key: 'requestTimestamp',
      render: (timestamp: Date) => moment(timestamp).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'State',
      dataIndex: 'deviceStatus',
      key: 'deviceStatus',
      render: (status: string) => {
        if (!status) return <Tag>N/A</Tag>;
        const colorMap: Record<string, string> = {
          POSTPONED: 'orange',
          PENDING: 'blue',
          UP_TO_DATE: 'green',
          NO_STATUS: 'default',
        };
        return <Tag color={colorMap[status] || 'default'}>{status.replace(/_/g, ' ')}</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right' as const,
      width: 100,
      render: (_: unknown, record: RequestTrackingResponse) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => onShowDetail(record)} size="small">
          Details
        </Button>
      ),
    },
  ];

  return (
    <Card
      title={
        <Row justify="space-between" align="middle">
          <Col>{viewMode === 'slowest' ? 'Top Slowest Requests' : 'Error Requests'}</Col>
          <Col>
            <Select value={viewMode} onChange={onViewModeChange} style={{ width: 180 }} loading={loading}>
              <Option value="slowest">Slowest Requests</Option>
              <Option value="errors">Error Requests</Option>
            </Select>
          </Col>
        </Row>
      }
    >
      <Table
        dataSource={requests}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: onPageChange,
        }}
        scroll={{ x: true }}
        locale={{
          emptyText:
            viewMode === 'slowest'
              ? 'No slow requests found in this time range'
              : 'No error requests found in this time range',
        }}
      />
    </Card>
  );
};

export default RequestsTable;
